import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import type {
	LoadComplianceDetail,
	ShipmentManagement,
} from "~/shared/schemas/shipment-management-schema.ts";
import { loadComplianceDetailSchema } from "~/shared/schemas/shipment-management-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";

const getEdiStatus = async ({
	poNumber,
	customerId,
}: {
	poNumber: string;
	customerId: string;
}): Promise<LoadComplianceDetail> => {
	const params = new URLSearchParams({ poNumber, customerId });
	const url = `${apiRoutes.GETEDISTATUS}?${params.toString()}`;

	const resp = await getAndValidate(url, loadComplianceDetailSchema);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const useGetEdiStatus = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();
	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation<
		LoadComplianceDetail,
		Error,
		{ poNumber: string; customerId: string }
	>({
		mutationFn: (variables) => getEdiStatus(variables),
		onSuccess: (loadComplianceDetail) => {
			queryClient.setQueriesData<ShipmentManagement>(
				{ queryKey: ["shipment"] },
				(shipment) =>
					shipment ? { ...shipment, loadComplianceDetail } : shipment,
			);
			showSuccess("EDI status refreshed.");
		},
		onError: (err) => {
			showError(
				err instanceof Error
					? err.message
					: "Failed to refresh EDI status. Please try again.",
			);
		},
	});

	return {
		getEdiStatus: mutate,
		getEdiStatusAsync: mutateAsync,
		loadComplianceDetail: data,
		isLoadingEdiStatus: isPending,
		error,
		resetEdiStatus: reset,
	};
};
